import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { AppUser } from "@/context/AuthContext";
import AnimatedModal from "@/components/base/AnimatedModal";

interface AppliedJobsTabProps {
  user: AppUser;
}

interface ApplicationRow {
  id: string;
  job_id: string;
  status: string;
  applied_at: string;
  jobs: {
    id: string;
    title: string;
    company: string;
    location: string;
    salary: string;
    type: string;
    description: string;
    detail: string;
    requirements: string[];
    benefits: string[];
  } | null;
}

const statusConfig: Record<string, { label: string; bg: string; text: string; icon: string }> = {
  pending: {
    label: "Chờ duyệt",
    bg: "bg-amber-50",
    text: "text-amber-700",
    icon: "ri-time-line",
  },
  reviewed: {
    label: "Nhà tuyển dụng đã xem hồ sơ",
    bg: "bg-blue-50",
    text: "text-blue-700",
    icon: "ri-eye-line",
  },
  interview: {
    label: "Đã duyệt hồ sơ và chờ phỏng vấn",
    bg: "bg-green-50",
    text: "text-green-700",
    icon: "ri-phone-line",
  },
  accepted: {
    label: "Trúng tuyển",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    icon: "ri-check-line",
  },
  rejected: {
    label: "Từ chối",
    bg: "bg-red-50",
    text: "text-red-700",
    icon: "ri-close-line",
  },
};

export default function AppliedJobsTab({ user }: AppliedJobsTabProps) {
  const [applications, setApplications] = useState<ApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailJob, setDetailJob] = useState<ApplicationRow["jobs"] | null>(null);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("vietnamcv_applications")
        .select("id, job_id, status, applied_at, vietnamcv_jobs!inner(id, title, company, location, salary, type, description, detail, requirements, benefits)")
        .eq("user_id", user.id)
        .order("applied_at", { ascending: false });

      if (err) {
        setError(err.message);
      } else {
        setApplications((data as ApplicationRow[]) || []);
      }
    } catch {
      setError("Không thể tải dữ liệu. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [user.id]);

  const typeLabels: Record<string, string> = {
    "full-time": "Toàn thời gian",
    "part-time": "Bán thời gian",
    contract: "Hợp đồng",
  };

  const openDetail = (job: ApplicationRow["jobs"]) => {
    setDetailJob(job);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-20 bg-background-100 rounded-xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600 mb-4">{error}</p>
        <button
          onClick={fetchApplications}
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          Thử lại
        </button>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-file-list-3-line text-3xl text-foreground-300"></i>
        </div>
        <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-2">
          Chưa có đơn ứng tuyển nào
        </h3>
        <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
          Hãy tìm kiếm công việc phù hợp và nộp hồ sơ ứng tuyển. CV của bạn sẽ được gửi đến nhà tuyển dụng.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
        >
          <i className="ri-search-line text-base"></i>
          <span>Tìm việc ngay</span>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-foreground-500 mb-4">
        Bạn đã ứng tuyển <strong className="text-foreground-800">{applications.length}</strong> công việc
      </p>

      <div className="space-y-3">
        {applications.map((app) => {
          const st = statusConfig[app.status] || statusConfig.pending;
          const job = app.jobs;
          return (
            <div
              key={app.id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-background-300/70 transition-all"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-briefcase-line text-primary-600"></i>
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-heading font-semibold text-sm text-foreground-900 truncate">
                      {job?.title || "Không rõ"}
                    </h4>
                    <p className="text-xs text-foreground-500 truncate">
                      {job?.company || ""}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {job?.location && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-foreground-500">
                          <i className="ri-map-pin-line text-[10px]"></i>
                          {job.location}
                        </span>
                      )}
                      {job?.salary && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-accent-600 font-medium">
                          <i className="ri-money-dollar-circle-line text-[10px]"></i>
                          {job.salary}
                        </span>
                      )}
                      {job?.type && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-foreground-400">
                          <i className="ri-briefcase-4-line text-[10px]"></i>
                          {typeLabels[job.type] || job.type}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 sm:flex-shrink-0">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${st.bg} ${st.text}`}>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`${st.icon} text-[11px]`}></i>
                  </div>
                  {st.label}
                </span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">
                  {new Date(app.applied_at).toLocaleDateString("vi-VN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </span>
                <button
                  onClick={() => openDetail(job)}
                  className="flex items-center gap-1 px-3 py-1.5 border border-background-300 rounded-full text-xs font-medium text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Chi tiết</span>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-[10px]"></i>
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Job Detail Modal */}
      <AnimatedModal isOpen={!!detailJob} onClose={() => setDetailJob(null)} variant="fade-up">
        <div className="bg-background-50 rounded-2xl w-[92vw] max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
          <div className="sticky top-0 bg-background-50 flex items-center justify-between px-6 py-4 border-b border-background-200 z-10">
            <div>
              <h2 className="font-heading font-bold text-lg text-foreground-950">{detailJob?.title}</h2>
              <p className="text-sm text-foreground-500">{detailJob?.company}</p>
            </div>
            <button
              onClick={() => setDetailJob(null)}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-foreground-500"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {detailJob && (
            <div className="p-6 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                {detailJob.location && (
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-600 bg-secondary-100/70 px-2.5 py-1 rounded-full">
                    <i className="ri-map-pin-line text-xs"></i>
                    {detailJob.location}
                  </span>
                )}
                {detailJob.salary && (
                  <span className="inline-flex items-center gap-1 text-xs text-accent-700 bg-accent-100/70 px-2.5 py-1 rounded-full font-medium">
                    <i className="ri-money-dollar-circle-line text-xs"></i>
                    {detailJob.salary}
                  </span>
                )}
                {detailJob.type && (
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-500 bg-background-200/70 px-2.5 py-1 rounded-full">
                    <i className="ri-briefcase-4-line text-xs"></i>
                    {typeLabels[detailJob.type] || detailJob.type}
                  </span>
                )}
              </div>

              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                  <i className="ri-file-text-line text-primary-500"></i>
                  Mô tả công việc
                </h4>
                <p className="text-sm text-foreground-700 leading-relaxed">{detailJob.description}</p>
              </div>

              {detailJob.detail && detailJob.detail !== detailJob.description && (
                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-article-line text-primary-500"></i>
                    Mô tả chi tiết
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">{detailJob.detail}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-accent-600"></i>
                    Yêu cầu
                  </h4>
                  <ul className="space-y-1.5">
                    {(detailJob.requirements || []).map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2 flex items-center gap-2">
                    <i className="ri-gift-line text-primary-500"></i>
                    Quyền lợi
                  </h4>
                  <ul className="space-y-1.5">
                    {(detailJob.benefits || []).map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-star-line text-primary-400 mt-0.5 flex-shrink-0"></i>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t border-background-200 flex justify-end">
                <button
                  onClick={() => setDetailJob(null)}
                  className="px-5 py-2.5 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Đóng
                </button>
              </div>
            </div>
          )}
        </div>
      </AnimatedModal>
    </div>
  );
}